// utils/errorDetail.js
/**
 * Único lugar que lee un `detail` de error del backend, en sus tres formas:
 * el objeto canónico `{code, message}`, un string suelto (endpoints legacy),
 * o el array de Pydantic que deja pasar un servicio sin exception handler.
 *
 * El array va primero: `typeof [] === 'object'`, así que en la rama de objeto
 * devolvería el fallback en vez del error de validación real.
 *
 * @param {unknown} detail - error.response.data.detail
 * @returns {string}
 */
export function extractDetailMessage(detail) {
  if (Array.isArray(detail)) {
    return detail[0]?.msg || 'An error occurred';
  }
  if (detail && typeof detail === 'object') {
    return detail.message || 'An error occurred';
  }
  return String(detail);
}

// Claves del sobre canónico. Lo que no está aquí es contexto del error.
const ENVELOPE_KEYS = new Set(['code', 'message', 'request_id', 'errors', 'context']);

/**
 * El `detail` completo: `code` estable para ramificar, `message` como respaldo
 * y `context` con los datos del caso.
 *
 * `account_service` anida el contexto bajo `context`; `auth_service` lo aplana
 * sobre el `detail` (`response_builder.py` hace `detail.update(context)`), que
 * es lo que documenta el contrato compartido de `error_mapping.py` y lo que ya
 * leen las vistas de Database. Ambos atienden estas rutas durante la
 * migración, así que se aceptan las dos formas.
 *
 * La rama aplanada —y con ella `ENVELOPE_KEYS`, que es una denylist y por tanto
 * cede en cuanto el backend agregue una clave de sobre nueva— se borra cuando
 * auth_service deje de atender estas rutas. No antes.
 *
 * @param {unknown} detail - error.response.data.detail
 * @returns {{code: string|null, message: string, context: object|null}}
 */
export function extractDetailError(detail) {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
    return { code: null, message: extractDetailMessage(detail), context: null };
  }

  const message = detail.message || 'An error occurred';
  const code = detail.code ?? null;

  if (detail.context && typeof detail.context === 'object') {
    return { code, message, context: detail.context };
  }

  const flattened = {};
  for (const [key, value] of Object.entries(detail)) {
    if (!ENVELOPE_KEYS.has(key)) flattened[key] = value;
  }
  return {
    code,
    message,
    context: Object.keys(flattened).length > 0 ? flattened : null,
  };
}
